<template>
  <div class="vip-page-content">
    <!-- header: back, title, page actions -->
    <div class="content-header">
      <button
        class="btn-back"
        title="Back to pages"
        @click="goBack"
      >
        <md-icon>arrow_back</md-icon>
      </button>
      <div class="content-header__title">
        <h3>Edit VIP Page</h3>
        <p>Edit the page per language, preview it, then save or publish.</p>
      </div>
      <div class="actions">
        <generic-button
          class="c-vip-action c-vip-action--danger"
          variation="delete"
          @click="deleteVipPage"
        >
          Delete
        </generic-button>
        <generic-button
          class="c-vip-action"
          variation="delete"
          @click="deactivateVipPage"
        >
          Deactivate
        </generic-button>
        <generic-button
          class="c-vip-action"
          variation="green"
          @click="publishVipPage"
        >
          Publish
        </generic-button>
        <generic-button
          class="c-vip-action"
          variation="red"
          @click="saveVipPageContent"
        >
          Save
        </generic-button>
      </div>
    </div>

    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <div class="page-name-section">
      <label class="name-label u-typography-color">Page Name</label>
      <div class="name-input-wrapper">
        <input
          v-model.trim="pageName"
          type="text"
          class="name-input"
          placeholder="Enter page name"
        >
        <span v-if="nameError" class="name-error">{{ nameError }}</span>
      </div>
    </div>

    <!-- code + preview, same layout as the multi-language code fields (e.g. Website > Footer) -->
    <div
      class="c-vip-editor"
      :class="{ 'is-fullscreen': isFullscreen, 'is-mobile': isMobileView }"
    >
      <div
        v-if="isFullscreen"
        class="c-vip-editor__fullscreen-header"
      >
        <h3>{{ pageName || 'VIP page' }}</h3>
        <button
          type="button"
          class="ui-btn ui-btn--secondary"
          @click="toggleFullscreen"
        >
          Done
        </button>
      </div>

      <div class="c-vip-editor__toolbar">
        <span class="c-vip-editor__toolbar-label">Language</span>
        <md-tabs
          class="c-tabs c-tabs--small"
          :md-active-tab.sync="selectedLanguage"
        >
          <md-tab
            v-for="language in languages"
            :id="language"
            :key="`lang-${language}`"
            :md-label="language"
            @click="changeLanguage(language)"
          />
        </md-tabs>
      </div>

      <div class="c-vip-editor__card">
        <div class="c-vip-editor__header">
          <app-tabs
            label="Code"
            :tabs="codeTabs"
            :value="codeTab"
            @input="codeTab = $event"
          />

          <div class="c-vip-editor__header-actions">
            <template v-if="codeTab === 'preview'">
              <button
                type="button"
                class="btn-device-toggle"
                title="Toggle mobile preview"
                @click="toggleMobileView"
              >
                <md-icon>{{ isMobileView ? 'phone_iphone' : 'devices' }}</md-icon>
                {{ isMobileView ? 'Mobile' : 'Desktop' }}
              </button>
              <generic-button
                class="btn-preview-run"
                variation="red"
                @click="updatePreview"
              >
                RUN
              </generic-button>
            </template>
            <button
              type="button"
              class="btn-fullscreen"
              :title="isFullscreen ? 'Exit full screen' : 'Full screen'"
              @click="toggleFullscreen"
            >
              <md-icon>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</md-icon>
            </button>
          </div>
        </div>

        <!-- all editors stay mounted (v-show): switching tabs never drops a pending edit -->
        <div
          v-show="codeTab === 'html'"
          class="editor-container"
        >
          <code-editor
            v-if="editContent[selectedLanguage]"
            :value="editContent[selectedLanguage].html"
            language="html"
            :dark-theme="isNightMode"
            height="350px"
            @input="editContent[selectedLanguage].html = $event"
          />
        </div>

        <div
          v-show="codeTab === 'css'"
          class="editor-container"
        >
          <code-editor
            v-if="editContent[selectedLanguage]"
            :value="editContent[selectedLanguage].css"
            language="css"
            :dark-theme="isNightMode"
            height="350px"
            @input="editContent[selectedLanguage].css = $event"
          />
        </div>

        <div
          v-show="codeTab === 'javascript'"
          class="editor-container"
        >
          <code-editor
            :value="javascript"
            language="javascript"
            :dark-theme="isNightMode"
            height="350px"
            @input="javascript = $event"
          />
        </div>

        <div
          v-show="codeTab === 'preview'"
          class="live-preview__container"
        >
          <iframe
            class="live-preview__frame"
            :srcdoc="previewHtml"
            sandbox="allow-scripts allow-same-origin allow-popups"
          />
        </div>

        <div class="c-vip-editor__footer">
          <span>
            Editing <strong>{{ String(selectedLanguage || '').toUpperCase() }}</strong>
            <template v-if="codeTab === 'javascript'"> · JavaScript is shared by all languages</template>
          </span>
          <span v-if="codeTab === 'preview'">Press Run to refresh the preview with the latest HTML, CSS and JavaScript.</span>
        </div>
      </div>
    </div>

    <div
      v-if="isFullscreen"
      class="c-vip-editor__backdrop"
      @click="toggleFullscreen"
    />
  </div>
</template>

<script>
import GenericButton from '@/shared/buttons/GenericButton';
import ConfirmationModalComponent from '@/shared/confirmation-modal/ConfirmationModalComponent';
import AreYouSureModal from '@/shared/are-you-sure-modal/AreYouSureModalComponent';
import CodeEditor from '@/shared/code-editor/CodeEditor';
import AppTabs from '@/shared/app-tabs/AppTabs';
import { vipService } from '@/services/vip';
import { historyLoggerMixin } from '@/mixins/historyLoggerMixin';
import { jsonParse } from '@/helpers/transformers';
import { mapActions, mapGetters } from 'vuex';
import { onlinePermissionsMixin } from '../../../../../mixins/onlinePermissionsMixin';

export default {
  name: 'VipPageContentPage',

  components: {
    GenericButton,
    CodeEditor,
    AppTabs,
    // eslint-disable-next-line vue/no-unused-components
    ConfirmationModalComponent,
    // eslint-disable-next-line vue/no-unused-components
    AreYouSureModal,
  },

  mixins: [historyLoggerMixin, onlinePermissionsMixin],

  data() {
    return {
      websiteId: null,
      vipPageId: null,
      pageName: '',
      nameError: '',
      errorMessage: '',
      selectedLanguage: 'en',
      languages: [],
      editContent: {},
      javascript: '',
      pageStatus: 'DRAFT',
      previewHtml: '',
      isFullscreen: false,
      isMobileView: false,
      formChanged: false,
      originalContent: {},
      originalJavascript: '',
      originalPageName: '',
      // UI only: which pane of the code card is visible (all editors stay mounted)
      codeTab: 'html',
      codeTabs: [
        { key: 'html', label: 'HTML', icon: 'code' },
        { key: 'css', label: 'CSS', icon: 'palette' },
        { key: 'javascript', label: 'JavaScript', icon: 'javascript' },
        { key: 'preview', label: 'Preview', icon: 'visibility' },
      ],
    };
  },

  computed: {
    ...mapGetters('theme', ['isNightMode']),
  },

	watch: {
		editContent: {
			handler() {
				this.checkFormChanged();
			},
			deep: true,
		},
		javascript() {
			this.checkFormChanged();
		},
		pageName() {
			this.checkFormChanged();
		},
	},

  created() {
    this.vipPageId = this.$route.params.vipPageId;
    this.loadPageContent();
  },

  destroyed() {
    this.displayChangePagePrompt();
  },

  methods: {
    ...mapActions('response', ['handleSuccess']),

    async loadPageContent() {
      try {
        const response = await vipService.getVipPageById(this.vipPageId);
        const pageData = response.data || {};

        if (!pageData || !pageData.id) {
          this.goToVipMain();
          return;
        }

        const additionalLanguages = jsonParse(pageData.website?.betshop?.market?.additionalLanguages) || [];
        const defaultLanguage = pageData.website?.betshop?.market?.defaultLanguage || 'en';
        this.languages = [...new Set([defaultLanguage, ...additionalLanguages])];
        this.selectedLanguage = this.languages[0] || 'en';

        this.websiteId = pageData.websiteId || '';

        if (!this.canReadWebsite(this.websiteId)) {
          this.$router.push({ name: 'on-line-vip' });
        }
        this.pageName = pageData.name || '';
        this.pageStatus = pageData.status || 'DRAFT';
        this.editContent = this.initializeContent();
        this.javascript = pageData.javascript || '';

        if (pageData.htmlCss) {
          Object.keys(pageData.htmlCss).forEach((lang) => {
            this.$set(this.editContent, lang, pageData.htmlCss[lang]);
          });
        }

        if (!this.editContent[this.selectedLanguage]) {
          this.$set(this.editContent, this.selectedLanguage, { html: '', css: '' });
        }

        this.storeOriginalContent();
        this.updatePreview();
      } catch (error) {
        this.goToVipMain();
      }
    },

    storeOriginalContent() {
      this.originalContent = JSON.parse(JSON.stringify(this.editContent));
      this.originalJavascript = this.javascript;
      this.originalPageName = this.pageName;
    },

    checkFormChanged() {
      const contentChanged = JSON.stringify(this.editContent) !== JSON.stringify(this.originalContent);
      const jsChanged = this.javascript !== this.originalJavascript;
      const nameChanged = this.pageName !== this.originalPageName;

      this.formChanged = contentChanged || jsChanged || nameChanged;
    },

    initializeContent() {
      const content = {};
      this.languages.forEach((language) => {
        content[language] = {
          html: '',
          css: '',
        };
      });
      return content;
    },

    changeLanguage(language) {
      this.selectedLanguage = language;
      if (!this.editContent[language]) {
        this.$set(this.editContent, language, { html: '', css: '' });
      }
    },

    updatePreview() {
      const content = (this.editContent && this.editContent[this.selectedLanguage]) || {};
      const html = content.html || '';
      const css = content.css || '';
      const js = this.javascript || '';
      const scriptOpen = '<scr' + 'ipt>';
      const scriptClose = '</scr' + 'ipt>';

      this.previewHtml = '<!DOCTYPE html><html><head><meta charset="utf-8"><style>' + css + '</style></head><body>' + html + scriptOpen + js + scriptClose + '</body></html>';
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },

    toggleMobileView() {
      this.isMobileView = !this.isMobileView;
    },

    async saveVipPageContent() {
      this.errorMessage = '';
      if (!this.validatePageName()) {
        return;
      }

      try {
        const payload = {
          id: Number(this.vipPageId),
          websiteId: Number(this.websiteId),
          name: this.pageName,
          htmlCss: this.editContent,
          javascript: this.javascript,
          status: this.pageStatus,
          timestamp: new Date().toISOString(),
        };

        await vipService.updateVipContent(Number(this.vipPageId), payload);
        this.storeOriginalContent();
        this.formChanged = false;
        this.handleSuccess({ message: 'VIP page updated!' });

      } catch (error) {
        this.errorMessage = error.data?.message[0] || 'Error saving VIP page';
      }
    },

    async publishVipPage() {
      if (this.formChanged) {
        this.$modal.show(
          AreYouSureModal,
          {
            acceptFunction: async () => {
              await this.saveVipPageContent();
              this.showPublishModal();
            },
            cancelFunction: () => {},
            modalTitle: 'You have unsaved changes',
            modalMessage: '',
            modalMsgAlert: '',
            modalDescription: 'You need to save changes before publishing the page',
          },
          { height: 'auto', scrollable: false, width: "440px" }
        );
        return;
      }

      this.showPublishModal();
    },

    showPublishModal() {
      this.errorMessage = '';
      this.$modal.show(
        ConfirmationModalComponent,
        {
          confirmationMsg: 'Are you sure you want to publish this VIP Page?',
          acceptButtonText: 'PUBLISH',
          onAccept: this.confirmPublishVipPage,
          onClose: () => {},
        },
        { height: 'auto', scrollable: false, width: "440px" }
      );
    },

    async confirmPublishVipPage() {
      this.errorMessage = '';
      try {
        await vipService.publishVipPage(Number(this.vipPageId));
        this.pageStatus = 'PUBLISHED';
        this.handleSuccess({ message: 'VIP page published!' });
      } catch (error) {
        this.errorMessage = error.data?.message[0] || 'Error publishing VIP page';
      }
    },

    validatePageName() {
      this.nameError = '';

      if (!this.pageName || this.pageName === '') {
        this.nameError = 'Page name is required';
        return false;
      }

      return true;
    },

    deactivateVipPage() {
      this.$modal.show(
        ConfirmationModalComponent,
        {
          confirmationMsg: 'Are you sure you want to deactivate this VIP Page?',
          acceptButtonText: 'DEACTIVATE',
          onAccept: this.confirmDeactivateVipPage,
          onClose: () => {},
        },
        { height: 'auto', scrollable: false, width: "440px" }
      );
    },

    async confirmDeactivateVipPage() {
      this.errorMessage = '';
      try {
        await vipService.deactivateVipPage(Number(this.vipPageId));
        this.pageStatus = 'INACTIVE';
        this.handleSuccess({ message: 'VIP page deactivated!' });
      } catch (error) {
        this.errorMessage = error.data?.message[0] || 'Error deactivating VIP page';
      }
    },

    deleteVipPage() {
      this.storeOriginalContent();
      this.formChanged = false;

      this.$modal.show(
        ConfirmationModalComponent,
        {
          confirmationMsg: 'Are you sure you want to delete this VIP Page?',
          acceptButtonText: 'DELETE',
          onAccept: this.confirmDeleteVipPage,
          onClose: () => {},
        },
        { height: 'auto', scrollable: false, width: "440px" }
      );
    },

    async confirmDeleteVipPage() {
      this.errorMessage = '';
      try {
        await vipService.removeVipPage(Number(this.vipPageId));
        this.goBack();
        this.handleSuccess({ message: 'VIP page deleted!' });

      } catch (error) {
        this.errorMessage = error.data?.message[0] || 'Error deleting VIP page';
      }
    },

    goBack() {
      this.$router.push({
        name: 'on-line-vip-pages-list',
        params: {
          websiteId: this.websiteId,
        }
      });
    },

    goToVipMain() {
      this.$router.push({ name: 'on-line-vip' });
    },

    displayChangePagePrompt() {
      if (!this.formChanged) {
        return null;
      }

      this.$modal.show(
        AreYouSureModal,
        {
          acceptFunction: async () => {
            await this.saveVipPageContent();
          },
          cancelFunction: () => {
            this.storeOriginalContent();
            this.formChanged = false;
          },
          modalTitle: 'Do you want to save changes?',
          modalMessage: '',
          modalMsgAlert: '',
          modalDescription: 'If you choose not to save, your changes will be lost',
        },
        { height: 'auto', scrollable: false, width: "440px" }
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.vip-page-content {
  padding: 24px;
  font-family: var(--font-sans);
}

// ---------- header ----------
.content-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;

  .btn-back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;

    &:hover {
      background-color: var(--bg-hover);
      color: var(--text-primary);
    }

    .md-icon {
      color: inherit !important;
    }
  }

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
  }

  p {
    margin: 2px 0 0;
    font-size: 13px;
    color: var(--text-secondary);
  }
}

.content-header__title {
  flex: 1;
  min-width: 0;
}

// page actions: right of the title, secondary first, primary last
.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

// Delete / Deactivate as quiet secondary buttons (Delete in red text)
::v-deep .c-vip-action.btn-delete {
  border: 1px solid var(--border-strong) !important;
  background: var(--bg-surface) !important;
  color: var(--text-primary) !important;

  &:hover {
    background: var(--bg-subtle) !important;
  }
}

::v-deep .c-vip-action--danger.btn-delete {
  color: var(--danger-fg) !important;
}

.error-message {
  margin-bottom: 16px;
  padding: 10px 14px;
  border: 1px solid var(--danger-border);
  border-radius: var(--radius-md);
  background-color: var(--danger-bg);
  color: var(--danger-fg);
  font-size: 13px;
}

// ---------- page name ----------
.page-name-section {
  margin-bottom: 24px;

  .name-label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .name-input-wrapper {
    display: flex;
    flex-direction: column;
    max-width: 560px;
  }

  .name-input {
    height: 40px;
    padding: 0 12px;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    outline: none;
    background-color: var(--bg-surface);
    box-shadow: var(--shadow-xs);
    color: var(--text-primary);
    font-family: var(--font-sans);
    font-size: 14px;
    transition: border-color var(--duration-fast) ease, box-shadow var(--duration-fast) ease;

    &:hover {
      border-color: var(--text-muted);
    }

    &:focus {
      border-color: var(--brand-600);
      box-shadow: var(--focus-ring);
    }
  }

  .name-error {
    margin-top: 6px;
    color: var(--danger-fg);
    font-size: 12px;
  }
}

// ---------- code + preview card (mirrors MultiLanguageCodeField) ----------
.c-vip-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &.is-fullscreen {
    position: fixed;
    inset: 24px;
    z-index: $zIndexDialog + 1;
    padding: 24px;
    border-radius: var(--radius-xl);
    background-color: var(--bg-surface);
    box-shadow: var(--shadow-xl);

    .c-vip-editor__card {
      flex: 1;
      min-height: 0;
    }

    // editors and preview use all the space in full screen
    .editor-container,
    .live-preview__container {
      flex: 1;
      min-height: 0;
    }

    .editor-container ::v-deep .code-editor-cm,
    .editor-container ::v-deep .cm-editor {
      height: 100% !important;
    }

    .live-preview__container {
      height: auto;
    }
  }
}

.c-vip-editor__backdrop {
  position: fixed;
  inset: 0;
  z-index: $zIndexDialog;
  background: rgba(10, 11, 13, 0.55);
  backdrop-filter: blur(2px);
}

.c-vip-editor__fullscreen-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
  }
}

.c-vip-editor__toolbar {
  display: flex;
  flex-direction: column;
  gap: 6px;

  ::v-deep .c-tabs--small {
    border-bottom: none !important;

    > .md-tabs-navigation {
      padding: 0;
    }
  }
}

.c-vip-editor__toolbar-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

.c-vip-editor__card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background-color: var(--bg-surface);
  overflow: hidden;

  ::v-deep .code-editor-cm {
    border: none;
    border-radius: 0;
  }
}

.c-vip-editor__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-right: 8px;
  border-bottom: 1px solid var(--border-default);
  background-color: var(--bg-subtle);

  ::v-deep .c-app-tabs--underline {
    flex: 1;
    min-width: 0;
    border-bottom: none;

    .c-app-tabs__tab {
      height: 44px;
    }
  }
}

.c-vip-editor__header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-device-toggle,
.btn-fullscreen {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: var(--bg-hover);
    color: var(--text-primary);
  }

  .md-icon {
    color: inherit !important;
    font-size: 20px !important;
  }
}

.btn-fullscreen {
  width: 34px;
  padding: 0;
  justify-content: center;
}

::v-deep .btn-preview-run {
  height: 32px !important;
  min-width: 0 !important;
  padding: 0 14px !important;
}

.editor-container {
  display: flex;
  flex-direction: column;
}

// ---------- preview ----------
.live-preview__container {
  display: flex;
  justify-content: center;
  width: 100%;
  height: 500px;
  overflow: auto;
  background-color: var(--bg-subtle);
}

.live-preview__frame {
  width: 100%;
  height: 100%;
  border: 0;
  background-color: #fff;
}

// mobile preview: phone-width frame
.c-vip-editor.is-mobile .live-preview__container {
  padding: 16px 0;
}

.c-vip-editor.is-mobile .live-preview__frame {
  width: 375px;
  max-width: 100%;
  border: 1px solid var(--border-strong);
  border-radius: 20px;
  box-shadow: var(--shadow-lg);
}

.c-vip-editor__footer {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 16px;
  border-top: 1px solid var(--border-default);
  background-color: var(--bg-subtle);
  font-size: 12px;
  color: var(--text-muted);

  strong {
    font-weight: 600;
    color: var(--text-secondary);
  }
}
</style>
